import React, { createContext, useContext, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Minus, Plus, Trash2, Car } from "lucide-react";
import {
  submitReservation, submitEventBooking, submitCatering, submitOrder,
} from "@/lib/api";
import { MENU, EVENTS, LOCATIONS } from "@/data/cafe";

const DialogContext = createContext(null);
export const useDialogs = () => useContext(DialogContext);

const field =
  "rounded-sm border-[#1A1514]/15 bg-[#F5F2EB] focus-visible:ring-[#1C7C54] focus-visible:ring-1 focus-visible:ring-offset-0 font-body";
const labelCls = "font-accent text-xs uppercase tracking-[0.15em] text-[#1A1514]/70 mb-1.5 block";

const primaryBtn =
  "rounded-sm bg-[#1C7C54] text-[#F5F2EB] hover:bg-[#14603F] font-accent uppercase tracking-[0.15em] text-xs transition-colors";

function Shell({ title, subtitle, children, testid }) {
  return (
    <DialogContent
      data-testid={testid}
      className="bg-[#F5F2EB] border-[#1A1514]/10 rounded-sm max-w-lg max-h-[90vh] overflow-y-auto chat-scroll"
    >
      <DialogHeader>
        <DialogTitle className="font-display text-3xl text-[#1A1514] font-medium">{title}</DialogTitle>
        <DialogDescription className="font-body text-[#1A1514]/60">{subtitle}</DialogDescription>
      </DialogHeader>
      {children}
    </DialogContent>
  );
}

function SubmitBtn({ loading, label, testid }) {
  return (
    <Button type="submit" disabled={loading} data-testid={testid} className={`${primaryBtn} w-full h-11 mt-2`}>
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : label}
    </Button>
  );
}

/* -------------------- Reservation -------------------- */
function ReservationForm({ onDone }) {
  const [loading, setLoading] = useState(false);
  const [guests, setGuests] = useState("2");
  const [location, setLocation] = useState(LOCATIONS[0].name);
  const submit = async (e) => {
    e.preventDefault();
    const f = e.target;
    setLoading(true);
    try {
      await submitReservation({
        name: f.name.value, phone: f.phone.value, email: f.email.value || null,
        location, date: f.date.value, time: f.time.value, guests: parseInt(guests),
        occasion: f.occasion.value || null, notes: f.notes.value || null,
      });
      toast.success("Table requested! We'll confirm shortly. ☕");
      onDone();
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4 pt-2" data-testid="reservation-form">
      <div>
        <Label className={labelCls}>Café Location</Label>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className={field} data-testid="res-location"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#F5F2EB] rounded-sm">
            {LOCATIONS.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 mt-2" data-testid="res-availability">
          <span className="h-2 w-2 rounded-full bg-[#1C7C54] animate-pulse" />
          <span className="font-body text-xs text-[#1C7C54]">Tables available today · instant confirmation on WhatsApp</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className={labelCls}>Name</Label><Input name="name" required className={field} data-testid="res-name" /></div>
        <div><Label className={labelCls}>Phone</Label><Input name="phone" required className={field} data-testid="res-phone" /></div>
      </div>
      <div><Label className={labelCls}>Email (optional)</Label><Input name="email" type="email" className={field} data-testid="res-email" /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div><Label className={labelCls}>Date</Label><Input name="date" type="date" required className={field} data-testid="res-date" /></div>
        <div><Label className={labelCls}>Time</Label><Input name="time" type="time" required className={field} data-testid="res-time" /></div>
        <div>
          <Label className={labelCls}>Guests</Label>
          <Select value={guests} onValueChange={setGuests}>
            <SelectTrigger className={field} data-testid="res-guests"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#F5F2EB] rounded-sm">
              {[1,2,3,4,5,6,7,8,10,12].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div><Label className={labelCls}>Occasion (optional)</Label><Input name="occasion" placeholder="Birthday, date night..." className={field} data-testid="res-occasion" /></div>
      <div><Label className={labelCls}>Notes</Label><Textarea name="notes" className={field} rows={2} data-testid="res-notes" /></div>
      <div className="flex items-start gap-2 rounded-sm bg-[#1C7C54]/8 border border-[#1C7C54]/20 px-3 py-2.5" data-testid="res-parking-note">
        <Car className="h-4 w-4 text-[#1C7C54] mt-0.5 shrink-0" />
        <p className="font-body text-xs text-[#1A1514]/75">Free parking* up to 60 min — or free parking on orders above ₹499.</p>
      </div>
      <SubmitBtn loading={loading} label="Reserve Table" testid="res-submit" />
    </form>
  );
}

/* -------------------- Event Booking -------------------- */
function EventForm({ onDone, presetType }) {
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState(presetType || EVENTS[0].type);
  const [location, setLocation] = useState(LOCATIONS[0].name);
  const submit = async (e) => {
    e.preventDefault();
    const f = e.target;
    setLoading(true);
    try {
      await submitEventBooking({
        name: f.name.value, phone: f.phone.value, email: f.email.value || null,
        location, event_type: type, date: f.date.value, guests: parseInt(f.guests.value || "1"),
        message: f.message.value || null,
      });
      toast.success("Event enquiry sent! Our team will reach out. 🎉");
      onDone();
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4 pt-2" data-testid="event-form">
      <div>
        <Label className={labelCls}>Café Location</Label>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className={field} data-testid="event-location"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#F5F2EB] rounded-sm">
            {LOCATIONS.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className={labelCls}>Event Type</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className={field} data-testid="event-type"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#F5F2EB] rounded-sm">
            {EVENTS.map((ev) => <SelectItem key={ev.type} value={ev.type}>{ev.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className={labelCls}>Name</Label><Input name="name" required className={field} data-testid="event-name" /></div>
        <div><Label className={labelCls}>Phone</Label><Input name="phone" required className={field} data-testid="event-phone" /></div>
      </div>
      <div><Label className={labelCls}>Email (optional)</Label><Input name="email" type="email" className={field} data-testid="event-email" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className={labelCls}>Preferred Date</Label><Input name="date" type="date" required className={field} data-testid="event-date" /></div>
        <div><Label className={labelCls}>Guests</Label><Input name="guests" type="number" min="1" defaultValue="20" required className={field} data-testid="event-guests" /></div>
      </div>
      <div><Label className={labelCls}>Message</Label><Textarea name="message" rows={3} className={field} data-testid="event-message" /></div>
      <SubmitBtn loading={loading} label="Send Enquiry" testid="event-submit" />
    </form>
  );
}

/* -------------------- Catering -------------------- */
function CateringForm({ onDone }) {
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const f = e.target;
    setLoading(true);
    try {
      await submitCatering({
        name: f.name.value, company: f.company.value || null, phone: f.phone.value,
        email: f.email.value, date: f.date.value, headcount: parseInt(f.headcount.value || "1"),
        details: f.details.value || null,
      });
      toast.success("Catering request received! We'll email a quote. ☕");
      onDone();
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4 pt-2" data-testid="catering-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className={labelCls}>Name</Label><Input name="name" required className={field} data-testid="cater-name" /></div>
        <div><Label className={labelCls}>Company (optional)</Label><Input name="company" className={field} data-testid="cater-company" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className={labelCls}>Phone</Label><Input name="phone" required className={field} data-testid="cater-phone" /></div>
        <div><Label className={labelCls}>Email</Label><Input name="email" type="email" required className={field} data-testid="cater-email" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className={labelCls}>Date</Label><Input name="date" type="date" required className={field} data-testid="cater-date" /></div>
        <div><Label className={labelCls}>Headcount</Label><Input name="headcount" type="number" min="1" defaultValue="30" required className={field} data-testid="cater-headcount" /></div>
      </div>
      <div><Label className={labelCls}>Details</Label><Textarea name="details" rows={3} placeholder="Menu preferences, timings, location..." className={field} data-testid="cater-details" /></div>
      <SubmitBtn loading={loading} label="Request Quote" testid="cater-submit" />
    </form>
  );
}

/* -------------------- Online Order -------------------- */
function OrderForm({ onDone }) {
  const [loading, setLoading] = useState(false);
  const [orderType, setOrderType] = useState("pickup");
  const [orderLocation, setOrderLocation] = useState(LOCATIONS[0].name);
  const { cart, incItem, decItem, cartItems, cartTotal, clearCart } = useDialogs();
  const flat = MENU.flatMap((c) => c.subcategories.flatMap((s) => s.items.map((i) => ({ ...i, cat: c.label }))));

  const submit = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) { toast.error("Add at least one item to your order."); return; }
    const f = e.target;
    setLoading(true);
    try {
      await submitOrder({
        customer_name: f.name.value, phone: f.phone.value, items: cartItems,
        location: orderLocation,
        order_type: orderType, address: orderType === "delivery" ? f.address.value : null,
        notes: f.notes.value || null,
      });
      toast.success(`Order placed! Total ₹${cartTotal}. We're on it. ☕`);
      clearCart();
      onDone();
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 pt-2" data-testid="order-form">
      <div>
        <Label className={labelCls}>Café Location</Label>
        <Select value={orderLocation} onValueChange={setOrderLocation}>
          <SelectTrigger className={field} data-testid="order-location"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#F5F2EB] rounded-sm">
            {LOCATIONS.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="max-h-[45vh] sm:max-h-64 overflow-y-auto chat-scroll border border-[#1A1514]/10 rounded-sm divide-y divide-[#1A1514]/8">
        {flat.map((i) => (
          <div key={i.name} className="flex items-center justify-between px-3 py-2">
            <div className="min-w-0">
              <p className="font-body text-sm text-[#1A1514] truncate">{i.name}</p>
              <p className="font-accent text-xs text-[#1C7C54]">₹{i.price}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button type="button" onClick={() => decItem(i.name)} className="h-7 w-7 grid place-items-center border border-[#1A1514]/15 rounded-sm hover:bg-[#E5DFD3] transition-colors" data-testid={`order-minus-${i.name}`}>
                <Minus className="h-3 w-3" />
              </button>
              <span className="w-5 text-center font-accent text-sm" data-testid={`order-qty-${i.name}`}>{cart[i.name]?.qty || 0}</span>
              <button type="button" onClick={() => incItem(i.name, i.price)} className="h-7 w-7 grid place-items-center border border-[#1A1514]/15 rounded-sm hover:bg-[#E5DFD3] transition-colors" data-testid={`order-plus-${i.name}`}>
                <Plus className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between px-1">
        <span className="font-accent uppercase tracking-[0.15em] text-xs text-[#1A1514]/60">Total</span>
        <span className="font-display text-2xl text-[#1A1514]" data-testid="order-total">₹{cartTotal}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className={labelCls}>Name</Label><Input name="name" required className={field} data-testid="order-name" /></div>
        <div><Label className={labelCls}>Phone</Label><Input name="phone" required className={field} data-testid="order-phone" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={labelCls}>Type</Label>
          <Select value={orderType} onValueChange={setOrderType}>
            <SelectTrigger className={field} data-testid="order-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#F5F2EB] rounded-sm">
              <SelectItem value="pickup">Pickup</SelectItem>
              <SelectItem value="delivery">Delivery</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {orderType === "delivery" && (
          <div><Label className={labelCls}>Address</Label><Input name="address" required className={field} data-testid="order-address" /></div>
        )}
      </div>
      <div><Label className={labelCls}>Notes</Label><Textarea name="notes" rows={2} className={field} data-testid="order-notes" /></div>
      <SubmitBtn loading={loading} label="Place Order" testid="order-submit" />
    </form>
  );
}

/* -------------------- Provider -------------------- */
export function DialogProvider({ children }) {
  const [active, setActive] = useState(null); // 'reservation' | 'order' | 'event' | 'catering'
  const [presetType, setPresetType] = useState(null);
  const open = (which, opts = {}) => { setPresetType(opts.eventType || null); setActive(which); };
  const close = () => setActive(null);

  // ---- Shared cart ----
  const [cart, setCart] = useState({}); // { name: { price, qty } }
  const incItem = (name, price) =>
    setCart((c) => ({ ...c, [name]: { price: c[name]?.price ?? price, qty: (c[name]?.qty || 0) + 1 } }));
  const decItem = (name) =>
    setCart((c) => {
      const n = { ...c };
      if (!n[name]) return n;
      const q = n[name].qty - 1;
      if (q <= 0) delete n[name]; else n[name] = { ...n[name], qty: q };
      return n;
    });
  const addToCart = (item) => {
    incItem(item.name, item.price);
    toast.success(`${item.name} added to cart · ₹${item.price}`);
  };
  const clearCart = () => setCart({});
  const cartItems = Object.entries(cart).map(([name, v]) => ({ name, price: v.price, quantity: v.qty }));
  const cartCount = cartItems.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);

  const value = { open, close, cart, addToCart, incItem, decItem, clearCart, cartItems, cartCount, cartTotal };

  return (
    <DialogContext.Provider value={value}>
      {children}

      <Dialog open={active === "reservation"} onOpenChange={(o) => !o && close()}>
        <Shell title="Reserve a Table" subtitle="Tell us when you're coming — we'll keep it cozy." testid="reservation-dialog">
          <ReservationForm onDone={close} />
        </Shell>
      </Dialog>

      <Dialog open={active === "order"} onOpenChange={(o) => !o && close()}>
        <Shell title="Order Online" subtitle="Build your order for pickup or delivery." testid="order-dialog">
          <OrderForm onDone={close} />
        </Shell>
      </Dialog>

      <Dialog open={active === "event"} onOpenChange={(o) => !o && close()}>
        <Shell title="Book an Event" subtitle="Open mics to corporate meetups — let's plan it." testid="event-dialog">
          <EventForm onDone={close} presetType={presetType} />
        </Shell>
      </Dialog>

      <Dialog open={active === "catering"} onOpenChange={(o) => !o && close()}>
        <Shell title="Catering Request" subtitle="Coffee & bites, delivered to your team." testid="catering-dialog">
          <CateringForm onDone={close} />
        </Shell>
      </Dialog>
    </DialogContext.Provider>
  );
}
